import { DeleteResponse, RepositioryErrorCode, RepositoryResponseCode, RepositoryResponseConflictCodes, RepositoryResponseHandler, SaveConflictResponse, SaveResponseResult } from '@skylab-shared/core';
import { AxiosResponse } from 'axios';

type DictionaryRepositoryResponseCode = 'CANNOT_DELETE_DEFAULT_ENTITY' | 'NON_UNIQUE_ENTITY';

type DictionaryRepositoryResponse = {
    status: 400,
    title: string,
    detail: string,
    instance: string,
    errors: [
        {
            field: string,
            message: string,
            code: DictionaryRepositoryResponseCode;
        }
    ]
}

export class DictionaryRepositoryResponseHandler implements RepositoryResponseHandler {
    public mapSaveResponse(response: AxiosResponse, code: RepositoryResponseCode): SaveResponseResult {
        switch (code) {
            case RepositoryResponseCode.Success:
                return this.mapSaveSuccess(response);
            case RepositoryResponseCode.Conflict:
                return this.mapSaveConflict(response);
            case RepositoryResponseCode.Forbidden:
            case RepositoryResponseCode.OutOfDateData:
            case RepositoryResponseCode.ValidationError:
            case RepositoryResponseCode.DataNotFound:
            case RepositoryResponseCode.ExternalError:
            default:
                return { code };
        }
    }

    public mapDeleteResponse(response: AxiosResponse, code: RepositoryResponseCode, _: boolean): DeleteResponse {
        const body = response?.data as DictionaryRepositoryResponse;
        return {
            responseCode: code,
            deletedSuccessfully: response.status >= 200 && response.status < 300,
            errorCode: this.mapDeleteErrorCode(body?.errors?.[0]?.code)
        };
    }

    private mapSaveSuccess(response: AxiosResponse): SaveConflictResponse<unknown> {
        const body = response.data ?? {};
        return {
            code: RepositoryResponseCode.Success,
            object: {
                externalId: body.externalId,
                version: body.version,
                ...body
            }
        };
    }

    private mapSaveConflict(response: AxiosResponse<DictionaryRepositoryResponse>): SaveConflictResponse<unknown> {
        const body = response.data;
        const conflicts = body?.errors;
        if (!conflicts?.length) {
            return { code: RepositoryResponseCode.Conflict };
        }

        return {
            code: RepositoryResponseCode.Conflict,
            conflicts: [{
                code: this.mapConflictErrorCode(conflicts[0].code),
                objectProperties: [conflicts[0].field?.toLowerCase()]
            }]
        };
    }

    private mapConflictErrorCode(code: DictionaryRepositoryResponseCode | undefined): RepositoryResponseConflictCodes | DictionaryRepositoryResponseCode | undefined {
        if (code === 'NON_UNIQUE_ENTITY') {
            return RepositoryResponseConflictCodes.UniqueConstraintViolation;
        }

        return code;
    }

    private mapDeleteErrorCode(code: DictionaryRepositoryResponseCode | undefined): RepositioryErrorCode | DictionaryRepositoryResponseCode | undefined {
        if (code === 'CANNOT_DELETE_DEFAULT_ENTITY') {
            return RepositioryErrorCode.CannotDeleteDictionaryDefaultItem;
        }

        return code;
    }
}

export const dictionaryRepositoryResponseHandler: RepositoryResponseHandler = new DictionaryRepositoryResponseHandler();
